"use client";

import { useEffect, useRef, useMemo, useState } from "react";
import Link from "next/link";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useDebounce, useIsDebouncing } from "@/lib/useDebounce";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { usePageTitle } from "@/lib/usePageTitle";
import {
  SearchIcon,
  UsersIcon,
  OnlineIcon,
  ServerIcon,
  GlobeIcon,
  TrendingIcon,
  ArrowRightIcon,
  CopyIcon,
  CheckIcon,
  TagIcon,
  StarIcon,
  StarFilledIcon,
  LinkIcon,
  ArrowLeftIcon,
} from "@/components/icons";
import { useAuth } from "@clerk/clerk-react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: Record<string, unknown>
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

// --- Types ---

type PublicOrg = {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  serverIp?: string;
  serverPort?: number;
  serverName?: string;
  memberCount: number;
  onlineCount: number;
  voteCount?: number;
  activityScore: number;
  isFeatured: boolean;
  status?: "online" | "offline";
  playerCount?: number;
  maxPlayers?: number;
  gameMode?: string;
};

type PublicListing = {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  serverIp?: string;
  serverPort?: number;
  gameMode?: string;
  tags?: string[];
  isFeatured?: boolean;
  status: "active" | "pending_claim" | "claimed" | "archived";
  // Live status from server status checker cron
  isOnline?: boolean;
  playerCount?: number;
  maxPlayers?: number;
  lastStatusCheckedAt?: number;
};

type ServerEntry = {
  kind: "org" | "listing";
  id: string;
  slug: string;
  name: string;
  description?: string;
  serverIp?: string;
  serverPort?: number;
  tags: string[];
  gameMode?: string;
  memberCount: number;
  voteCount: number;
  onlineCount: number;
  /** Normalised from org.status or listing.isOnline */
  isOnline: boolean;
  playerCount?: number;
  maxPlayers?: number;
  lastStatusCheckedAt?: number;
  isFeatured: boolean;
  serverName?: string;
  // org-only
  orgId?: string;
};

// --- Tag helpers ---
// Tags only come from an explicit gameMode mapping — no keyword mining.

const GAME_MODE_TAG_MAP: Record<string, string[]> = {
  "mini games": ["Minigames"],
  minigames: ["Minigames"],
  pve: ["PvE"],
  pvp: ["PvP"],
  economy: ["Economy"],
  factions: ["Factions", "PvP"],
  mmo: ["MMO", "RPG"],
  mcmmo: ["MCMMO", "RPG"],
  creative: ["Creative"],
  anarchy: ["Anarchy", "PvP"],
  survival: ["Survival"],
  hardcore: ["Hardcore", "Survival"],
  "bed wars": ["Bed Wars", "PvP"],
  kitpvp: ["KitPvP", "PvP"],
};

function tagsFromGameMode(gameMode?: string): string[] {
  if (!gameMode) return [];
  const key = gameMode.toLowerCase().replace(/[^a-z0-9\s]+/g, " ").trim();
  const mapped = GAME_MODE_TAG_MAP[key];
  if (mapped) return mapped;
  // Capitalise unrecognised gameMode as a single tag
  const label = gameMode
    .split(" ")
    .map((w) => {
      const n = w.toLowerCase();
      if (n === "pvp") return "PvP";
      if (n === "pve") return "PvE";
      if (n === "mmo") return "MMO";
      if (n === "smp") return "SMP";
      return n.charAt(0).toUpperCase() + n.slice(1);
    })
    .join(" ");
  return [label];
}

function normalizeTag(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

// --- Turnstile widget ---

function TurnstileWidget({
  onVerify,
}: {
  onVerify: (token: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      // No site key configured — skip CAPTCHA (dev/testing mode)
      onVerify("dev-bypass");
      return;
    }

    const render = () => {
      if (!containerRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile!.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: onVerify,
        size: "compact",
        theme: "auto",
      });
    };

    if (window.turnstile) {
      render();
    } else if (!document.querySelector("#cf-turnstile-script")) {
      const script = document.createElement("script");
      script.id = "cf-turnstile-script";
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.onload = render;
      document.head.appendChild(script);
    } else {
      // Script is loading; poll until ready
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          render();
        }
      }, 100);
      return () => clearInterval(interval);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={containerRef} />;
}

// --- Vote button ---

function VoteButton({
  orgId,
  voteCount,
  hasVoted,
  onVoted,
}: {
  orgId: string;
  voteCount: number;
  hasVoted: boolean;
  onVoted: () => void;
}) {
  const { isSignedIn } = useAuth();
  const castVote = useAction(api.votes.castVote);
  const [state, setState] = useState<
    "idle" | "captcha" | "submitting" | "done" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleToken(token: string) {
    setState("submitting");
    const result = await castVote({
      organizationId: orgId as Parameters<typeof castVote>[0]["organizationId"],
      captchaToken: token,
    });
    if (result.ok) {
      setState("done");
      onVoted();
    } else {
      setState("error");
      setErrorMsg(result.error ?? "Vote failed");
    }
  }

  if (hasVoted || state === "done") {
    return (
      <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
        <StarFilledIcon className="h-3.5 w-3.5" />
        Voted · {voteCount}
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <Link
        href="/signup"
        className="flex items-center gap-1.5 text-xs text-muted hover:text-foreground transition-colors"
      >
        <StarIcon className="h-3.5 w-3.5" />
        {voteCount} · Sign in to vote
      </Link>
    );
  }

  if (state === "captcha") {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted">Complete the CAPTCHA to vote</p>
        <TurnstileWidget onVerify={handleToken} />
        <button
          onClick={() => setState("idle")}
          className="text-xs text-muted hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    );
  }

  if (state === "submitting") {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted">
        <StarIcon className="h-3.5 w-3.5 animate-pulse" />
        Submitting…
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="space-y-1">
        <p className="text-xs text-destructive">{errorMsg}</p>
        <button
          onClick={() => setState("idle")}
          className="text-xs text-muted hover:text-foreground"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setState("captcha")}
      className="flex items-center gap-1.5 text-xs text-muted hover:text-primary transition-colors"
      title="Vote for this server (once per day)"
    >
      <StarIcon className="h-3.5 w-3.5" />
      {voteCount} · Upvote
    </button>
  );
}

// --- Main page ---

export default function ServersPage() {
  usePageTitle("Hytale Servers");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const isSearching = useIsDebouncing(searchQuery, debouncedSearch);
  const [copiedIp, setCopiedIp] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  // Optimistic set of org IDs voted this session (avoids re-querying)
  const [sessionVotes, setSessionVotes] = useState<Set<string>>(new Set());

  const organizations = useQuery(api.organizations.listPublic, { limit: 150 });
  const listings = useQuery(api.serverListings.listPublic, { limit: 100 });
  const recentVotes = useQuery(api.votes.getMyRecentVotes, {});

  const votedOrgIds = useMemo(() => {
    const set = new Set(recentVotes ?? []);
    for (const id of sessionVotes) set.add(id);
    return set;
  }, [recentVotes, sessionVotes]);

  // Normalise both sources into a single ServerEntry list
  const allEntries = useMemo((): ServerEntry[] | undefined => {
    if (organizations === undefined || listings === undefined) return undefined;

    const orgEntries: ServerEntry[] = (organizations as PublicOrg[]).map((org) => ({
      kind: "org",
      id: org._id,
      slug: org.slug,
      name: org.name,
      description: org.description,
      serverIp: org.serverIp,
      serverPort: org.serverPort,
      serverName: org.serverName,
      tags: tagsFromGameMode(org.gameMode),
      gameMode: org.gameMode,
      memberCount: org.memberCount,
      voteCount: org.voteCount ?? 0,
      onlineCount: org.onlineCount,
      isOnline: org.status === "online",
      playerCount: org.playerCount,
      maxPlayers: org.maxPlayers,
      isFeatured: org.isFeatured,
      orgId: org._id,
    }));

    // Only show active/claimed listings not already represented by an org
    const orgSlugs = new Set(orgEntries.map((e) => e.slug));
    const listingEntries: ServerEntry[] = (listings as PublicListing[])
      .filter((l) => l.status === "active" && !orgSlugs.has(l.slug))
      .map((l) => ({
        kind: "listing",
        id: l._id,
        slug: l.slug,
        name: l.name,
        description: l.description,
        serverIp: l.serverIp,
        serverPort: l.serverPort,
        tags: l.tags ?? tagsFromGameMode(l.gameMode),
        gameMode: l.gameMode,
        memberCount: 0,
        voteCount: 0,
        onlineCount: 0,
        isOnline: l.isOnline === true,
        playerCount: l.playerCount,
        maxPlayers: l.maxPlayers,
        lastStatusCheckedAt: l.lastStatusCheckedAt,
        isFeatured: l.isFeatured ?? false,
      }));

    // Unified sort: featured → online → playerCount desc → voteCount+memberCount desc
    const combined = [...orgEntries, ...listingEntries];
    combined.sort((a, b) => {
      if (a.isFeatured !== b.isFeatured) return Number(b.isFeatured) - Number(a.isFeatured);
      if (a.isOnline !== b.isOnline) return Number(b.isOnline) - Number(a.isOnline);
      const aPlayers = a.playerCount ?? 0;
      const bPlayers = b.playerCount ?? 0;
      if (aPlayers !== bPlayers) return bPlayers - aPlayers;
      return (b.voteCount + b.memberCount) - (a.voteCount + a.memberCount);
    });
    return combined;
  }, [organizations, listings]);

  const availableTags = useMemo(() => {
    if (!allEntries) return [];
    const counts = new Map<string, { tag: string; count: number }>();
    for (const entry of allEntries) {
      const seen = new Set<string>();
      for (const tag of entry.tags) {
        const key = normalizeTag(tag);
        if (seen.has(key)) continue;
        seen.add(key);
        const cur = counts.get(key);
        if (cur) {
          cur.count += 1;
        } else {
          counts.set(key, { tag, count: 1 });
        }
      }
    }
    return Array.from(counts.entries())
      .map(([key, v]) => ({ key, tag: v.tag, count: v.count }))
      .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag))
      .slice(0, 20);
  }, [allEntries]);

  const filteredEntries = useMemo(() => {
    if (!allEntries) return undefined;
    const q = debouncedSearch.trim().toLowerCase();
    const tagSet = new Set(selectedTags);

    let entries = allEntries.filter((entry) => {
      if (q) {
        const matches =
          entry.name.toLowerCase().includes(q) ||
          entry.description?.toLowerCase().includes(q) ||
          entry.gameMode?.toLowerCase().includes(q) ||
          (entry.serverName ?? "").toLowerCase().includes(q) ||
          entry.tags.some((t) => t.toLowerCase().includes(q));
        if (!matches) return false;
      }
      if (tagSet.size > 0) {
        const entryTagKeys = entry.tags.map(normalizeTag);
        return Array.from(tagSet).some((k) => entryTagKeys.includes(k));
      }
      return true;
    });

    if (tagSet.size > 0) {
      entries = [...entries].sort((a, b) => {
        const aMatches = a.tags.filter((t) => tagSet.has(normalizeTag(t))).length;
        const bMatches = b.tags.filter((t) => tagSet.has(normalizeTag(t))).length;
        if (bMatches !== aMatches) return bMatches - aMatches;
        if (a.isFeatured !== b.isFeatured) return Number(b.isFeatured) - Number(a.isFeatured);
        if (a.isOnline !== b.isOnline) return Number(b.isOnline) - Number(a.isOnline);
        const aPlayers = a.playerCount ?? 0;
        const bPlayers = b.playerCount ?? 0;
        if (aPlayers !== bPlayers) return bPlayers - aPlayers;
        return (b.voteCount + b.memberCount) - (a.voteCount + a.memberCount);
      });
    }

    return entries;
  }, [allEntries, debouncedSearch, selectedTags]);

  const toggleTag = (key: string) => {
    setSelectedTags((cur) =>
      cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]
    );
  };

  const copyToClipboard = async (ip: string) => {
    await navigator.clipboard.writeText(ip);
    setCopiedIp(ip);
    setTimeout(() => setCopiedIp(null), 2000);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        <section className="border-b border-border bg-foreground/5 py-12">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <GlobeIcon className="h-8 w-8" />
              </div>
              <div>
                <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
                  Hytale Server Finder
                  <InfoHint text="Browse public organizations and find active servers to join." />
                </h1>
                <p className="text-muted">
                  Discover gaming communities and servers
                </p>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <div className="relative flex-1">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                <Input
                  type="text"
                  placeholder="Search servers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-8"
                  aria-label="Search servers"
                />
                {isSearching && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg className="h-4 w-4 animate-spin text-muted" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                    </svg>
                  </span>
                )}
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="rounded-md px-3 text-sm text-muted hover:text-foreground transition-colors border border-border"
                  aria-label="Clear search"
                >
                  Clear
                </button>
              )}
            </div>

            {availableTags.length > 0 && (
              <div className="mt-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedTags([])}
                    className={`rounded-full px-3 py-1 text-sm transition-colors ${
                      selectedTags.length === 0
                        ? "bg-primary text-primary-foreground"
                        : "bg-foreground/5 text-muted hover:bg-foreground/10"
                    }`}
                  >
                    All
                  </button>
                  {availableTags.map(({ key, tag, count }) => (
                    <button
                      key={key}
                      onClick={() => toggleTag(key)}
                      className={`rounded-full px-3 py-1 text-sm transition-colors ${
                        selectedTags.includes(key)
                          ? "bg-primary text-primary-foreground"
                          : "bg-foreground/5 text-muted hover:bg-foreground/10"
                      }`}
                    >
                      <TagIcon className="mr-1 inline-block h-3 w-3" />
                      {tag} ({count})
                    </button>
                  ))}
                </div>
                {selectedTags.length > 0 && (
                  <p className="mt-2 text-xs text-muted">
                    Showing servers matching any selected tag — more tag matches rank higher.
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="py-8">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            {filteredEntries === undefined ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-48" />
                ))}
              </div>
            ) : filteredEntries.length === 0 ? (
              <div className="py-20 text-center">
                <GlobeIcon className="mx-auto h-12 w-12 text-muted" />
                <h2 className="mt-4 text-xl font-semibold text-foreground">
                  No servers found
                </h2>
                <p className="mt-2 text-muted">
                  {debouncedSearch || selectedTags.length > 0
                    ? "Try a different search or filter"
                    : "Be the first to create a public organization!"}
                </p>
                <Link href="/signup" className="mt-6 inline-block">
                  <Button>Create Your Organization</Button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filteredEntries.map((entry) => (
                  <ServerCard
                    key={entry.id}
                    entry={entry}
                    copiedIp={copiedIp}
                    onCopyIp={copyToClipboard}
                    hasVoted={votedOrgIds.has(entry.id)}
                    onVoted={() =>
                      setSessionVotes((s) => new Set([...s, entry.id]))
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// --- Server Card ---
// The entire card is clickable — clicking anywhere navigates to the detail page.
// Interactive elements (Copy IP, Vote button, Claim link) stop propagation so
// they still work independently without triggering the card navigation.

function ServerCard({
  entry,
  copiedIp,
  onCopyIp,
  hasVoted,
  onVoted,
}: {
  entry: ServerEntry;
  copiedIp: string | null;
  onCopyIp: (ip: string) => void;
  hasVoted: boolean;
  onVoted: () => void;
}) {
  const serverAddress = entry.serverIp
    ? entry.serverPort
      ? `${entry.serverIp}:${entry.serverPort}`
      : entry.serverIp
    : null;

  const resolvedServerName = entry.serverName || entry.name;
  const detailHref = entry.kind === "org" ? `/org/${entry.slug}` : `/servers/${entry.slug}`;

  return (
    // Outer wrapper: relative + group for hover. The Link overlay covers the whole
    // card for full-card click, while interactive children use z-10 + stopPropagation.
    <div className="relative group">
      {/* Full-card click overlay — sits behind interactive elements */}
      <Link
        href={detailHref}
        className="absolute inset-0 z-0 rounded-lg"
        aria-label={`View details for ${entry.name}`}
        tabIndex={-1}
      />

      <Card className="relative cursor-pointer overflow-hidden transition-all duration-200 group-hover:bg-foreground/[0.03] group-hover:border-primary/30 group-hover:shadow-md group-hover:-translate-y-0.5">
        {entry.isFeatured && (
          <div className="absolute right-2 top-2 z-10">
            <Badge className="bg-primary text-primary-foreground">
              <TrendingIcon className="mr-1 h-3 w-3" />
              Featured
            </Badge>
          </div>
        )}

        <CardHeader className="pb-2">
          <CardTitle className="flex items-start justify-between pr-6">
            <span className="text-lg group-hover:text-primary transition-colors">{entry.name}</span>
          </CardTitle>
          {entry.description && (
            <p className="line-clamp-2 text-sm text-muted">{entry.description}</p>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {entry.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {entry.tags.slice(0, 4).map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {entry.tags.length > 4 && (
                <Badge variant="secondary" className="text-xs">
                  +{entry.tags.length - 4}
                </Badge>
              )}
            </div>
          )}

          <div className="grid gap-2 rounded-lg border border-border/70 bg-foreground/[0.02] p-3 text-xs">
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-1 uppercase tracking-wide text-muted">
                Server Name
                <InfoHint text="The in-game server label shown in connection and listing contexts." />
              </span>
              <span className="font-medium text-foreground">{resolvedServerName}</span>
            </div>
            {entry.kind === "org" ? (
              resolvedServerName === entry.name && (
                <p className="text-[11px] text-muted">
                  Server name matches organization name.
                </p>
              )
            ) : (
              <p className="text-[11px] text-muted">
                Listing name shown until this server is claimed and linked.
              </p>
            )}
          </div>

          {entry.kind === "listing" && (
            <div className="relative z-10 flex items-center gap-1.5 text-xs text-muted">
              <LinkIcon className="h-3 w-3" />
              <span>Unclaimed listing — </span>
              <Link
                href={`/servers/${entry.slug}/claim`}
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                claim this server
              </Link>
            </div>
          )}

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            {entry.kind === "org" && (
              <>
                <div className="flex items-center gap-1 text-muted">
                  <UsersIcon className="h-4 w-4" />
                  <span>{entry.memberCount} members</span>
                </div>
                <div className="flex items-center gap-1 text-primary">
                  <OnlineIcon className="h-4 w-4" />
                  <span>{entry.onlineCount} online</span>
                </div>
              </>
            )}
            {entry.lastStatusCheckedAt && (
              <Badge
                variant={entry.isOnline ? "default" : "secondary"}
                className={entry.isOnline ? "bg-emerald-600 text-white" : ""}
              >
                <span
                  className={`mr-1.5 inline-block h-2 w-2 rounded-full ${
                    entry.isOnline ? "bg-white" : "bg-muted-foreground/50"
                  }`}
                />
                {entry.isOnline ? "Online" : "Offline"}
              </Badge>
            )}
            {entry.isOnline &&
              (typeof entry.playerCount === "number" ||
                typeof entry.maxPlayers === "number") && (
                <Badge variant="outline">
                  {entry.playerCount ?? 0}/{entry.maxPlayers ?? "?"} players
                </Badge>
              )}
            {entry.gameMode && <Badge variant="outline">{entry.gameMode}</Badge>}
          </div>

          {/* Server IP — labeled Copy IP button */}
          {serverAddress && (
            <div className="relative z-10 flex items-center justify-between rounded-lg border border-border bg-background p-2">
              <div className="flex items-center gap-2">
                <ServerIcon className="h-4 w-4 text-muted" />
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted">
                    Address
                  </p>
                  <code className="text-sm font-medium">{serverAddress}</code>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCopyIp(serverAddress);
                }}
                aria-label={`Copy server address ${serverAddress}`}
              >
                {copiedIp === serverAddress ? (
                  <>
                    <CheckIcon className="h-3.5 w-3.5 text-primary" />
                    <span className="text-primary">Copied!</span>
                  </>
                ) : (
                  <>
                    <CopyIcon className="h-3.5 w-3.5" />
                    Copy IP
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Vote + View row — z-10 so they stay clickable above the link overlay */}
          <div className="relative z-10 flex items-center justify-between">
            {entry.kind === "org" ? (
              <span onClick={(e) => e.stopPropagation()}>
                <VoteButton
                  orgId={entry.id}
                  voteCount={entry.voteCount}
                  hasVoted={hasVoted}
                  onVoted={onVoted}
                />
              </span>
            ) : (
              <span />
            )}

            <Link
              href={detailHref}
              onClick={(e) => e.stopPropagation()}
            >
              <Button variant="outline" size="sm">
                {entry.kind === "org" ? "View" : "Details"}
                <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
